import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { ThreeDot } from "react-loading-indicators";
import swal from "sweetalert";
import { format } from "date-fns";
import { customStyles } from "../../../../assets/table-style";
import NoData from "../../../../assets/img/no-data.png";
import NoAccess from "../../../../assets/img/NoAccess.png";
import useDecodeToken from "../../../../hooks/customHook/useDecodeToken";
import { formatToPHP } from "../../../../utils/numberFormatter";
import { getCutoffs } from "../../../../services/Cutoff/getCutoff.service";
import { getRetainedNetIncomes } from "../../../../services/RetainedNetIncome/getRetainedNetIncomes.service";
import { getRetainedNetIncomeById } from "../../../../services/RetainedNetIncome/getRetainedNetIncomeById.service";
import { updateRetainedNetIncome } from "../../../../services/RetainedNetIncome/updateRetainedNetIncome.service";
import AddRetainedNetIncomeModal from "./AddRetainedNetIncomeModal";

const formatDate = (date) =>
  date ? format(new Date(date), "MMM dd, yyyy") : "--";

const NetIncomeAmount = ({ value }) => (
  <span className={value < 0 ? "text-danger" : "text-success"}>
    {formatToPHP(value)}
  </span>
);

const cutoffColumns = [
  {
    name: "Cutoff",
    selector: (row) => row.name,
    minWidth: "180px",
    cell: (row) => <div className="text-center fw-medium">{row.name}</div>,
  },
  {
    name: "Period",
    selector: (row) => row.from,
    minWidth: "220px",
    cell: (row) => (
      <div className="text-nowrap">
        {formatDate(row.from)} to {formatDate(row.to)}
      </div>
    ),
  },
  {
    name: "Net Income",
    selector: (row) => row.net_income,
    minWidth: "160px",
    cell: (row) => (
      <div className="fw-semibold">
        <NetIncomeAmount value={row.net_income} />
      </div>
    ),
  },
];

const toCutoffOption = (cutoff) => ({
  value: cutoff.id,
  label: cutoff.name,
  from: cutoff.from,
  to: cutoff.to,
});

// One labelled value in the details card
const Detail = ({ label, children }) => (
  <div className="col-12 col-md-6 col-lg-4 mb-3">
    <div className="text-muted small">{label}</div>
    <div className="fw-medium text-break">{children}</div>
  </div>
);

function ViewRetainedNetIncome({ authrztn }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const userLoggedID = useDecodeToken();

  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Edit modal
  const [showEdit, setShowEdit] = useState(false);
  const [validated, setValidated] = useState(false);
  const [form, setForm] = useState(null);
  const [cutoffOptions, setCutoffOptions] = useState([]);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const fetchRecord = async () => {
    setIsLoading(true);
    try {
      const data = await getRetainedNetIncomeById(id);
      setRecord(data);
    } catch (error) {
      console.log(error);
      setRecord(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRecord();
  }, [id]);

  const cutoffs = (record?.retained_net_income_cutoffs || [])
    .map((rnic) => rnic.cutoff)
    .filter(Boolean);

  // Posted cutoffs not used by another Retained Net Income (this one's own
  // cutoffs stay selectable)
  const fetchCutoffOptions = async () => {
    setIsOptionsLoading(true);
    try {
      const [allCutoffs, records] = await Promise.all([
        getCutoffs(),
        getRetainedNetIncomes(),
      ]);
      const usedByOthers = new Set(
        records
          .filter((item) => item.id !== id)
          .flatMap((item) =>
            item.retained_net_income_cutoffs.map((rnic) => rnic.cutoff_id),
          ),
      );
      setCutoffOptions(
        allCutoffs
          .filter((cutoff) => !usedByOthers.has(cutoff.id))
          .map(toCutoffOption),
      );
    } catch (error) {
      console.log(error);
    } finally {
      setIsOptionsLoading(false);
    }
  };

  const handleShowEdit = () => {
    setValidated(false);
    setForm({
      selectedCutoffs: cutoffs.map(toCutoffOption),
      transactionNumber: record.transaction_number,
      name: record.name,
      remarks: record.remarks || "",
    });
    setShowEdit(true);
    fetchCutoffOptions();
  };

  const handleCloseEdit = () => {
    if (isSavingRef.current) return;
    setShowEdit(false);
    setValidated(false);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSavingRef.current) return;
    setValidated(true);

    if (form.selectedCutoffs.length === 0 || !form.name.trim()) {
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in the red text fields.",
      });
      return;
    }

    swal({
      title: "Confirm Action",
      text: "Are you sure you want to update this Retained Net Income?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed || isSavingRef.current) return;
      isSavingRef.current = true;
      setIsSaving(true);

      const payload = {
        id,
        cutoff_ids: form.selectedCutoffs.map((cutoff) => cutoff.value),
        name: form.name.trim(),
        remarks: form.remarks.trim(),
        userLoggedID,
      };

      let result = null;
      try {
        result = await updateRetainedNetIncome(payload);
      } catch (error) {
        console.log(error);
        swal({
          icon: "error",
          title: "Something went wrong",
          text:
            error.response?.data?.message ||
            "Unable to update the Retained Net Income.",
        });
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }

      if (result) {
        swal({
          icon: "success",
          title: "Retained Net Income updated",
          text: result.message,
        });
        handleCloseEdit();
        fetchRecord();
      }
    });
  };

  if (!authrztn.includes("Monthly-View")) {
    return (
      <div className="no-access">
        <img src={NoAccess} alt="NoAccess" className="no-access-img" />
        <h3>You don't have access to this function.</h3>
      </div>
    );
  }

  const creator = record?.retained_net_income_created_masterlist;
  const canEdit = authrztn.includes("Monthly-Edit") && !!record;

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3">
            <Link
              to="/accounting/cutoff"
              className="text-dark mx-2"
              onClick={(e) => {
                // Go back to wherever this page was opened from (Cutoff or
                // Profit Distribution); fall back to Cutoff when there's no
                // in-app history, e.g. opened from a bookmark or new tab
                if (window.history.state?.idx > 0) {
                  e.preventDefault();
                  navigate(-1);
                }
              }}
            >
              <i className="fa-solid fa-arrow-left"></i>
            </Link>
            View Retained Net Income
          </span>
        </div>
        {canEdit && (
          <div className="d-flex gap-2">
            <Button
              className="btn d-flex align-items-center title-button"
              variant="primary"
              onClick={handleShowEdit}
            >
              <i className="bx bx-edit fs-5 me-1"></i> Edit
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="w-100 p-4 d-flex justify-content-center">
          <ThreeDot color="#0d6efd" size="medium" text="" textColor="" />
        </div>
      ) : !record ? (
        <div className="w-100 p-4 d-flex flex-column align-items-center">
          <img src={NoData} alt="No Data" style={{ width: "150px" }} />
          <span className="text-muted mt-2">
            Retained Net Income not found.
          </span>
        </div>
      ) : (
        <div className="container-fluid mt-3">
          <div
            className={`border rounded p-3 mb-3 ${
              record.net_income < 0
                ? "bg-danger bg-opacity-10"
                : "bg-success bg-opacity-10"
            }`}
          >
            <div className="text-muted small">
              Total Net Income ({cutoffs.length} cutoff
              {cutoffs.length === 1 ? "" : "s"})
            </div>
            <div className="fs-3 fw-bold">
              <NetIncomeAmount value={record.net_income} />
            </div>
          </div>

          <div className="border shadow-sm rounded p-3 mb-3">
            <div className="row">
              <Detail label="Transaction No.">
                {record.transaction_number}
              </Detail>
              <Detail label="Name">{record.name}</Detail>
              <Detail label="Period">
                {formatDate(record.from)} to {formatDate(record.to)}
              </Detail>
              <Detail label="Created By">
                {creator ? `${creator.fname} ${creator.lname}` : "--"}
              </Detail>
              <Detail label="Created At">{formatDate(record.createdAt)}</Detail>
              <Detail label="Last Updated">
                {formatDate(record.updatedAt)}
              </Detail>
              <div className="col-12">
                <div className="text-muted small">Remarks</div>
                <div className="fw-medium text-break">
                  {record.remarks || "--"}
                </div>
              </div>
            </div>
          </div>

          <h5 className="mb-2">Cutoffs</h5>
          <DataTable
            columns={cutoffColumns}
            data={record.cutoff_net_incomes}
            customStyles={customStyles}
            className="dataTable"
            noDataComponent={
              <div className="p-4 d-flex flex-column align-items-center">
                <img src={NoData} alt="No Data" style={{ width: "150px" }} />
                <span className="text-muted mt-2">No cutoffs found.</span>
              </div>
            }
          />
        </div>
      )}

      {form && (
        <AddRetainedNetIncomeModal
          show={showEdit}
          onClose={handleCloseEdit}
          onSubmit={handleSubmit}
          form={form}
          onFieldChange={handleFieldChange}
          validated={validated}
          cutoffOptions={cutoffOptions}
          isLoading={isOptionsLoading}
          isSaving={isSaving}
          title="Edit Retained Net Income"
          submitLabel="Save Changes"
        />
      )}
    </div>
  );
}

export default ViewRetainedNetIncome;
